import {
  GridActionsCellItem,
  GridColDef,
  GridRowId,
  GridRowModes,
  GridRowModesModel,
  GridActionsColDef,
  GridRowParams,
} from '@mui/x-data-grid';
import { Chip, Stack } from '@mui/material';
import { Timestamp } from 'firebase/firestore';

import DocumentDropzone from './DocumentDropzone';
import { DocumentLink } from '../../../styled/ChangeRequest.styled';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';

import { ChangeRequestStatus } from '../../../types';
import { getFileName } from '../../../utils/getFileName';
import { getFormattedDate } from '../../../utils/dateFormat';

const statuses = {
  pending: 'В процесі',
  completed: 'Завершено',
  testing: 'На перевірці',
} as const;

interface CreateChangeRequestsColumnsProps {
  rowModesModel: GridRowModesModel;
  handleEditClick: (id: GridRowId) => () => void;
  handleSaveClick: (id: GridRowId) => () => void;
  handleCancelClick: (id: GridRowId) => () => void;
  handleDeleteClick: (id: GridRowId, createdAt: Timestamp) => Promise<void>;
  isAdmin: boolean;
}

const createChangeRequestsColumns = ({
  rowModesModel,
  handleEditClick,
  handleSaveClick,
  handleCancelClick,
  handleDeleteClick,
  isAdmin,
}: CreateChangeRequestsColumnsProps): GridColDef[] => {
  return [
    {
      field: 'recordId',
      headerName: 'ID',
      headerAlign: 'center',
      align: 'center',
      width: 80,
      editable: isAdmin,
      type: 'number',
      renderCell: ({ value }) => (value ? value.toString() : ''),
      preProcessEditCellProps: (params) => {
        const hasError = validateRequiredNumericCell(params.props.value);
        return {
          ...params.props,
          error: hasError,
        };
      },
    },
    {
      field: 'createdAt',
      headerName: 'Дата запиту',
      headerAlign: 'center',
      align: 'center',
      width: 100,
      editable: isAdmin,
      type: 'date',
      valueGetter: (timestamp: any) => {
        if (!timestamp) return null;

        if (
          typeof timestamp.seconds === 'number' &&
          typeof timestamp.nanoseconds === 'number'
        ) {
          return new Timestamp(
            timestamp.seconds,
            timestamp.nanoseconds,
          ).toDate();
        }

        return timestamp;
      },
      renderCell: ({ value }) => (value ? getFormattedDate(value) : ''),
      preProcessEditCellProps: (params) => {
        const hasError = !params.props.value;
        return {
          ...params.props,
          error: hasError,
        };
      },
    },
    {
      field: 'requestType',
      headerName: 'Тип запиту',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      minWidth: 200,
      editable: isAdmin,
    },
    {
      field: 'status',
      headerName: 'Статус',
      headerAlign: 'center',
      align: 'center',
      width: 120,
      editable: isAdmin,
      type: 'singleSelect',
      valueOptions: Object.keys(statuses),
      getOptionLabel: (option) => statuses[option as ChangeRequestStatus],
      renderCell: ({ value }) => (
        <Chip size="small" {...getStatusChipProps(value)} />
      ),
    },
    {
      field: 'rowsCount',
      headerName: 'К-ть рядків',
      headerAlign: 'center',
      align: 'center',
      width: 100,
      editable: isAdmin,
      type: 'number',
      renderCell: ({ value }) =>
        value ? new Intl.NumberFormat('uk-UA').format(value) : '',
      preProcessEditCellProps: (params) => {
        const hasError = validateNegativeNumericCell(params.props.value);
        return {
          ...params.props,
          error: hasError,
        };
      },
    },
    {
      field: 'paymentAmount',
      headerName: 'Сума',
      headerAlign: 'center',
      align: 'center',
      width: 100,
      editable: isAdmin,
      type: 'number',
      renderCell: ({ value }) =>
        value ? new Intl.NumberFormat('uk-UA').format(value) : '',
      preProcessEditCellProps: (params) => {
        const hasError = validateNegativeNumericCell(params.props.value);
        return {
          ...params.props,
          error: hasError,
        };
      },
    },
    {
      field: 'paymentDate',
      headerName: 'Дата оплати',
      headerAlign: 'center',
      align: 'center',
      width: 100,
      editable: isAdmin,
      type: 'date',
      valueGetter: (timestamp: any) => {
        if (!timestamp) return null;

        if (
          typeof timestamp.seconds === 'number' &&
          typeof timestamp.nanoseconds === 'number'
        ) {
          return new Timestamp(
            timestamp.seconds,
            timestamp.nanoseconds,
          ).toDate();
        }

        return timestamp;
      },
      renderCell: ({ value }) => (value ? getFormattedDate(value) : ''),
    },
    {
      field: 'docs',
      headerName: 'Документи',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      minWidth: 140,
      editable: isAdmin,
      renderCell: ({ value }) => {
        return (
          <Stack gap={1}>
            {value.map((doc: string, index: number) => (
              <DocumentLink key={index} href={doc} target="_blank">
                {getFileName(doc)}
              </DocumentLink>
            ))}
          </Stack>
        );
      },
      renderEditCell: (params) => <DocumentDropzone {...params} />,
    },
    {
      field: 'comment',
      headerName: 'Коментар',
      headerAlign: 'center',
      align: 'center',
      width: 250,
      editable: isAdmin,
    },
    ...(isAdmin
      ? [
          {
            field: 'actions',
            type: 'actions' as GridActionsColDef['type'],
            headerName: 'Дії',
            width: 100,
            getActions: ({ id, row }: GridRowParams) => {
              const { createdAt } = row;
              const isInEditMode = rowModesModel[id]
                ? rowModesModel[id].mode === GridRowModes.Edit
                : false;

              if (isInEditMode) {
                return [
                  <GridActionsCellItem
                    icon={<SaveIcon />}
                    label="Зберегти"
                    onClick={handleSaveClick(id)}
                  />,
                  <GridActionsCellItem
                    icon={<CancelIcon />}
                    label="Відмінити"
                    onClick={handleCancelClick(id)}
                  />,
                ];
              }

              return [
                <GridActionsCellItem
                  icon={<EditIcon />}
                  label="Змінити"
                  onClick={handleEditClick(id)}
                />,
                <GridActionsCellItem
                  icon={<DeleteIcon />}
                  label="Видалити"
                  onClick={() => handleDeleteClick(id, createdAt)}
                />,
              ];
            },
          },
        ]
      : []),
  ];
};

const getStatusChipProps = (status: ChangeRequestStatus) => {
  switch (status) {
    case 'pending':
      return {
        label: statuses.pending,
      };
    case 'testing':
      return {
        label: statuses.testing,
        color: 'warning',
      } as const;
    case 'completed':
      return {
        label: statuses.completed,
        color: 'success',
      } as const;
    default:
      return {
        label: statuses.pending,
      };
  }
};

const validateRequiredNumericCell = (value: any): boolean => {
  return value === '' || value === null || isNaN(value) || value < 0;
};

const validateNegativeNumericCell = (value: any): boolean => {
  return value < 0;
};

export default createChangeRequestsColumns;
